import React, { useState, useRef, useEffect } from 'react';
import { CloseIcon, ImageIcon, UploadIcon } from './Icons';
import { API_URL } from '../config';
import articleService from '../services/articleService';
import '../styles/MediaUploadModal.css';

const MediaUploadModal = ({ isOpen, onClose, onInsert, editingData = null }) => {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [preview, setPreview] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [showCachedImages, setShowCachedImages] = useState(false);
  const [cachedImages, setCachedImages] = useState([]);
  const [selectedCachedImage, setSelectedCachedImage] = useState(null);
  const [isEditingCached, setIsEditingCached] = useState(false);
  
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  // 支持的文件格式（基于strapi默认配置）
  const acceptedTypes = {
    images: ['image/jpeg', 'image/png', 'image/gif', 'image/svg+xml', 'image/tiff', 'image/ico', 'image/dvju', 'image/webp'],
    videos: ['video/mpeg', 'video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/x-flv'],
    audios: ['audio/mpeg', 'audio/wav', 'audio/ogg']
  };

  const allAcceptedTypes = [...acceptedTypes.images, ...acceptedTypes.videos, ...acceptedTypes.audios];
  const acceptString = allAcceptedTypes.join(',');

  // 重置表单
  const resetForm = () => {
    if (editingData) {
      // 编辑模式：预填充数据
      setTitle(editingData.title || '');
      setDescription(editingData.description || '');
      if (editingData.file) {
        setPreview(editingData.file.url.startsWith('http') ? editingData.file.url : `${API_URL}${editingData.file.url}`);
      }
    } else {
      // 新建模式：清空表单
      setFile(null);
      setTitle('');
      setDescription('');
      setPreview(null);
    }
    setError('');
    setIsUploading(false);
    setSelectedCachedImage(null);
    setIsEditingCached(false);
  };

  // 初始化表单数据
  useEffect(() => {
    if (isOpen) {
      resetForm();
      loadCachedImages();
    }
  }, [isOpen, editingData]);

  // 加载缓存图片
  const loadCachedImages = () => {
    try {
      const cached = JSON.parse(localStorage.getItem('cachedImages') || '[]');
      setCachedImages(cached.reverse()); // 最新的在前面
    } catch (error) {
      console.error('加载缓存图片失败:', error);
      setCachedImages([]);
    }
  };

  // 关闭弹窗时重置，打开时加载缓存
  useEffect(() => {
    if (!isOpen) {
      resetForm();
      setShowCachedImages(false);
    } else {
      loadCachedImages();
    }
  }, [isOpen]);

  // 处理文件选择
  const handleFileSelect = (selectedFile) => {
    if (!selectedFile) return;

    // 验证文件类型
    if (!allAcceptedTypes.includes(selectedFile.type)) {
      setError('不支持的文件格式。支持的格式：图片(JPEG, PNG, GIF, SVG, TIFF, ICO, DVU, WebP)、视频(MPEG, MP4, QuickTime, WMV, AVI, FLV)、音频(MP3, WAV, OGG)');
      return;
    }

    // 验证文件大小 (10MB限制)
    if (selectedFile.size > 10 * 1024 * 1024) {
      setError('文件大小不能超过10MB');
      return;
    }

    setFile(selectedFile);
    setError('');

    // 生成预览
    if (selectedFile.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(selectedFile);
    } else {
      setPreview(null);
    }

    // 自动填充标题（去掉扩展名）
    if (!title) {
      const fileName = selectedFile.name;
      const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.')) || fileName;
      setTitle(nameWithoutExt);
    }
  };

  // 处理拖拽
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = e.dataTransfer.files;
    if (files && files[0]) {
      handleFileSelect(files[0]);
    }
  };

  // 处理粘贴上传
  const handlePaste = (e) => {
    const items = e.clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        const file = items[i].getAsFile();
        handleFileSelect(file);
        break;
      }
    }
  };

  // 监听粘贴事件
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('paste', handlePaste);
      return () => document.removeEventListener('paste', handlePaste);
    }
  }, [isOpen]);

  // 选择缓存图片
  const handleSelectCachedImage = (cachedImage) => {
    setSelectedCachedImage(cachedImage);
    setTitle(cachedImage.title || '');
    setDescription(cachedImage.description || '');
    setPreview(cachedImage.url.startsWith('http') ? cachedImage.url : `${API_URL}${cachedImage.url}`);
    setFile(null);
    setIsEditingCached(true);
    setShowCachedImages(false);
  };

  // 插入缓存图片
  const handleInsertCachedImage = () => {
    if (!selectedCachedImage) return;

    const mediaData = {
      __component: 'shared.media',
      file: {
        id: selectedCachedImage.id,
        name: selectedCachedImage.name,
        url: selectedCachedImage.url,
        mime: selectedCachedImage.mime,
        size: selectedCachedImage.size
      },
      title: title.trim(),
      description: description.trim()
    };

    // 更新缓存中的标题和描述
    try {
      const cached = JSON.parse(localStorage.getItem('cachedImages') || '[]');
      const index = cached.findIndex(img => img.id === selectedCachedImage.id);
      if (index >= 0) {
        cached[index].title = title.trim();
        cached[index].description = description.trim();
        localStorage.setItem('cachedImages', JSON.stringify(cached));
      }
    } catch (error) {
      console.error('更新缓存失败:', error);
    }

    onInsert(mediaData);
    onClose();
  };

  // 删除缓存图片
  const handleDeleteCachedImage = (imageId) => {
    try {
      const cached = JSON.parse(localStorage.getItem('cachedImages') || '[]');
      const filtered = cached.filter(img => img.id !== imageId);
      localStorage.setItem('cachedImages', JSON.stringify(filtered));
      setCachedImages(filtered.reverse());
      
      if (selectedCachedImage && selectedCachedImage.id === imageId) {
        resetForm();
      }
    } catch (error) {
      console.error('删除缓存图片失败:', error);
    }
  };

  // 处理仅更新标题和描述（编辑模式下无新文件上传）
  const handleUpdateOnly = () => {
    if (!editingData) return;
    
    const mediaData = {
      __component: 'shared.media',
      file: editingData.file,
      title: title.trim(),
      description: description.trim()
    };
    
    onInsert(mediaData);
    onClose();
  };

  // 上传文件
  const handleUpload = async () => {
    // 编辑模式下，如果没有选择新文件，只更新标题和描述
    if (editingData && !file) {
      handleUpdateOnly();
      return;
    }
    
    if (!file) {
      setError('请选择文件');
      return;
    }

    setIsUploading(true);
    setError('');

    try {
      const uploadedFile = await articleService.uploadMedia(file, 'Articles');
      
      // 创建媒体组件数据
      const mediaData = {
        __component: 'shared.media',
        file: {
          id: uploadedFile.id,
          name: uploadedFile.name,
          url: uploadedFile.url,
          mime: uploadedFile.mime,
          size: uploadedFile.size
        },
        title: title.trim(),
        description: description.trim()
      };

      // 缓存图片到本地存储
      if (uploadedFile.mime.startsWith('image/')) {
        try {
          const cachedImages = JSON.parse(localStorage.getItem('cachedImages') || '[]');
          const imageCache = {
            id: uploadedFile.id,
            name: uploadedFile.name,
            url: uploadedFile.url,
            mime: uploadedFile.mime,
            size: uploadedFile.size,
            title: title.trim(),
            description: description.trim(),
            cachedAt: new Date().toISOString()
          };
          
          // 避免重复缓存
          const existingIndex = cachedImages.findIndex(img => img.id === uploadedFile.id);
          if (existingIndex >= 0) {
            cachedImages[existingIndex] = imageCache;
          } else {
            cachedImages.push(imageCache);
          }
          
          // 限制缓存数量（最多100张）
          if (cachedImages.length > 100) {
            cachedImages.splice(0, cachedImages.length - 100);
          }
          
          localStorage.setItem('cachedImages', JSON.stringify(cachedImages));
        } catch (error) {
          console.error('缓存图片失败:', error);
        }
      }

      onInsert(mediaData);
      onClose();
    } catch (error) {
      console.error('上传失败:', error);
      setError('上传失败，请重试');
    } finally {
      setIsUploading(false);
    }
  };

  // 获取文件类型显示
  const getFileTypeDisplay = (type) => {
    if (type.startsWith('image/')) return '图片';
    if (type.startsWith('video/')) return '视频';
    if (type.startsWith('audio/')) return '音频';
    return '文件';
  };

  if (!isOpen) return null;

  return (
    <div className="media-upload-overlay" onClick={onClose}>
      <div className="media-upload-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-tabs">
            <button 
              className={`tab-btn ${!showCachedImages ? 'active' : ''}`}
              onClick={() => setShowCachedImages(false)}
            >
              上传新文件
            </button>
            <button 
              className={`tab-btn ${showCachedImages ? 'active' : ''}`}
              onClick={() => setShowCachedImages(true)}
            >
              已缓存图片 ({cachedImages.length})
            </button>
          </div>
          <button className="close-btn" onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        <div className="modal-body">
          {!showCachedImages ? (
            <>
              {/* 文件上传区域 */}
              <div 
                ref={dropZoneRef}
                className={`upload-zone ${dragActive ? 'drag-active' : ''} ${file || selectedCachedImage ? 'has-file' : ''}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => !selectedCachedImage && fileInputRef.current?.click()}
              >
                {file || selectedCachedImage ? (
                  <div className="file-selected">
                    {preview ? (
                      <img src={preview} alt="预览" className="file-preview" />
                    ) : (
                      <div className="file-icon">
                        <ImageIcon size={48} />
                      </div>
                    )}
                    {file && (
                      <button 
                        className="change-file-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          fileInputRef.current?.click();
                        }}
                      >
                        更换文件
                      </button>
                    )}
                    {selectedCachedImage && (
                      <button 
                        className="change-file-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          resetForm();
                        }}
                      >
                        重新选择
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="upload-prompt">
                    <UploadIcon size={48} />
                    <h4>选择或拖拽文件到此处</h4>
                    <p>支持图片、视频、音频文件，最大10MB</p>
                    <p className="paste-hint">💡 提示：您也可以直接粘贴图片 (Ctrl+V)</p>
                    <div className="supported-formats">
                      <div className="format-group">
                        <strong>图片:</strong> JPEG, PNG, GIF, SVG, TIFF, ICO, DVU, WebP
                      </div>
                      <div className="format-group">
                        <strong>视频:</strong> MPEG, MP4, QuickTime, WMV, AVI, FLV
                      </div>
                      <div className="format-group">
                        <strong>音频:</strong> MP3, WAV, OGG
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept={acceptString}
                onChange={(e) => handleFileSelect(e.target.files[0])}
                style={{ display: 'none' }}
              />

              {/* 错误信息 */}
              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}

              {/* 媒体信息表单 */}
              {(file || selectedCachedImage) && (
                <div className="media-form">
                  <div className="form-group">
                    <label htmlFor="media-title">标题</label>
                    <input
                      id="media-title"
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="输入媒体标题（可选）"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="media-description">描述</label>
                    <textarea
                      id="media-description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="输入媒体描述（可选）"
                      rows="3"
                    />
                  </div>
                </div>
              )}
            </>
          ) : (
            /* 缓存图片列表 */
            <div className="cached-images-container">
              {cachedImages.length === 0 ? (
                <div className="no-cached-images">
                  <ImageIcon size={48} />
                  <h4>暂无缓存图片</h4>
                  <p>上传的图片会自动缓存到这里，方便下次使用</p>
                </div>
              ) : (
                <div className="cached-images-grid">
                  {cachedImages.map((image) => (
                    <div 
                      key={image.id} 
                      className={`cached-image-item ${selectedCachedImage?.id === image.id ? 'selected' : ''}`}
                    >
                      <div className="cached-image-preview">
                        <img 
                          src={image.url.startsWith('http') ? image.url : `${API_URL}${image.url}`} 
                          alt={image.title || image.name}
                          onClick={() => handleSelectCachedImage(image)}
                        />
                        <button 
                          className="delete-cached-btn"
                          onClick={() => handleDeleteCachedImage(image.id)}
                          title="删除缓存"
                        >
                          <CloseIcon size={16} />
                        </button>
                      </div>
                      <div className="cached-image-info">
                        <p className="cached-image-title">{image.title || image.name}</p>
                        <p className="cached-image-size">{(image.size / 1024 / 1024).toFixed(2)} MB</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-cancel" onClick={onClose}>
            取消
          </button>
          {!showCachedImages ? (
            <button 
              className="btn-insert" 
              onClick={handleUpload}
              disabled={(!file && !editingData) || isUploading}
            >
              {isUploading ? '上传中...' : (editingData ? (file ? '替换媒体' : '更新信息') : '插入媒体')}
            </button>
          ) : (
            <button 
              className="btn-insert" 
              onClick={handleInsertCachedImage}
              disabled={!selectedCachedImage}
            >
              {editingData ? '替换为选中图片' : '插入选中图片'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default MediaUploadModal;